import SelectBuscable from "@/shared/components/SelectBuscable";
import { estiloDeBoton } from "@/shared/lib/estilosDeBoton";
import {
  getProjectsPublicAll,
  type Project,
} from "@/features/configuracion/api/projectService";
import { TituloDePantalla } from "@/shared/components/TituloDePantalla";
import { CapaDeModal } from "@/shared/components/CapaDeModal";
import { Checkbox } from "@/shared/components/Checkbox";
import { TextareaQueCrece } from "@/shared/components/TextareaQueCrece";
import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { config } from "@/config/env";
import {
  separaElLlamado,
  enUnaLinea,
} from "@/features/rh/lib/descripcionPublicada";
import { rangoSalarial } from "@/features/rh/lib/rangoSalarial";
import {
  ArrowLeft,
  Trash2,
  AlertCircle,
  Edit2,
  Save,
  Send,
  X,
} from "lucide-react";
import { apiService, JobRequestStatus } from "@/services/api";
import type { JobRequest } from "@/services/api";
import { StatusBadge } from "@/shared/components/StatusBadge";
import { RequestTimeline } from "@/features/rh/components/RequestTimeline";
import { ActionModal } from "@/shared/components/ActionModal";
import { RespondToReturned } from "@/features/rh/components/RespondToReturned";
import { CandidatePublicLink } from "@/features/rh/components/CandidatePublicLink";
import { JobRequestCandidatesTab } from "@/features/rh/components/JobRequestCandidatesTab";
import JobRequestActions from "@/features/rh/components/job-requests/JobRequestActions";
import BotonDuplicarSolicitud from "@/features/rh/components/job-requests/BotonDuplicarSolicitud";
import RichTextEditor from "@/shared/components/RichTextEditor";
import {
  MAXIMO_DE_VACANTES,
  MINIMO_DE_VACANTES,
  errorDeVacantes,
} from "@/features/rh/lib/numeroDeVacantes";
import { useAuth } from "@/shared/hooks/useAuth";
import { conRechazo } from "@/features/rh/lib/rechazoDeSolicitudes";
import { esHtmlVacio } from "@/features/rh/lib/descripcionDelPuesto";
import {
  canAssignJobRequestSalaries,
  canFinalApproveJobRequests,
  canPreApproveJobRequests,
  canRejectJobRequests,
  puedeVerCompensacion,
  userHasPermission,
} from "@/shared/lib/roleUtils";
import { formatDateSafe, formatDateTimeMX } from "@/shared/lib/dateUtils";
import {
  getAreasCatalog,
  type Area,
} from "@/features/configuracion/api/areaService";
import {
  MOTIVOS_DE_SOLICITUD,
  NIVELES_DE_ESCOLARIDAD,
  TIPOS_DE_CONTRATO,
  NIVELES_DE_INGLES,
  type NivelDeIngles,
  type SoftwareRequerido,
} from "@/features/rh/lib/catalogosDeRequisicion";
import { PaqueteriaRequerida } from "@/features/rh/components/PaqueteriaRequerida";
import { CandidatosInternos } from "@/features/rh/components/CandidatosInternos";
import { ConfirmacionDeInterno } from "@/features/rh/components/ConfirmacionDeInterno";
import {
  filasQueSeMandan,
  type CandidatoInterno,
} from "@/features/rh/lib/candidatosInternos";

/**
 * Las cuatro pestañas del detalle, en el orden del diseño.
 *
 * La clave viaja en la URL, así que un enlace a una pestaña sigue funcionando
 * al recargar. Las de antes —generales, enlace, candidatos— cambiaron de
 * nombre: una dirección vieja cae en «Perfil y requisitos» en vez de romperse.
 */
const PESTANAS = [
  { clave: "perfil", etiqueta: "Perfil y requisitos" },
  { clave: "compensacion", etiqueta: "Compensación" },
  { clave: "candidatos", etiqueta: "Candidatos" },
  { clave: "estado", etiqueta: "Estado de la solicitud y enlace" },
] as const;

type Pestana = (typeof PESTANAS)[number]["clave"];

/** La tarjeta del diseño: blanca, radio 12 y borde de un pixel. */
/**
 * La tarjeta del diseño.
 *
 * `bg-surface` y no `bg-white`: la hoja global tiñe todos los `bg-white` a
 * #fafbfc con `!important`, y sobre ese tono el gris de «Requisitos del
 * perfil» deja de distinguirse. El token vale blanco puro y nadie lo pisa.
 */
const TARJETA = "bg-surface rounded-xl border border-[#E5E5E5] p-6";

/**
 * El bloque gris dentro de una tarjeta, como «Requisitos del perfil».
 *
 * Es lo que separa las secciones en este diseño: cada grupo en su caja, no una
 * línea entre columnas.
 */
const CAJA_GRIS = "rounded-lg bg-[#F8F8F8] p-5";

const JobRequestDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const jobRequestId = Number(id);
  const { user } = useAuth();
  const [jobRequest, setJobRequest] = useState<JobRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAction, setSelectedAction] = useState<
    "review" | "approve" | "reject" | "return" | null
  >(null);
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * Al solicitante se le quita la pestaña entera, no solo los montos
   * (SRH-319). El servidor ya no le manda el rango, así que dejarla pintada
   * seria enseñarle etiquetas sin valor y hacerle creer que no hay sueldo
   * asignado.
   */
  const verCompensacion = puedeVerCompensacion(user || undefined);
  const pestanasVisibles = verCompensacion
    ? PESTANAS
    : PESTANAS.filter((p) => p.clave !== "compensacion");

  const activeTab = (
    pestanasVisibles.some((p) => p.clave === searchParams.get("tab"))
      ? searchParams.get("tab")
      : "perfil"
  ) as Pestana;
  const setActiveTab = (tab: Pestana) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true },
    );

  // Estados para edición de solicitud
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [puestos, setPuestos] = useState<any[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [isLoadingPuestos, setIsLoadingPuestos] = useState(false);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [editData, setEditData] = useState({
    puestoSolicitar: "",
    areaId: "",
    areaVacante: "",
    sede: "",
    prioridad: "media",
    numeroPlazas: "1",
    tipoPerfil: "operativo",
    tipoContrato: "tiempo_completo",
    motivo: "nueva",
    previousEmployeeName: "",
    fechaRequeridaInicio: "",
    entrevistaFinalSolicitante: false,
    requiresHardwareSoftware: false,
    requiresWorkstation: false,
    costCenter: "",
    proyectoId: "",
    travelPercentage: "",
    salarioMinimo: "",
    salarioMaximo: "",
    prestaciones: "",
    habilidadesMinimas: "",
    gradoAcademico: "licenciatura",
    englishLevel: null as NivelDeIngles | null,
    requiredSoftware: [] as SoftwareRequerido[],
    internalCandidates: [] as CandidatoInterno[],
    aniosExperiencia: "",
    certificacionesRequeridas: "",
    otrasCertificaciones: "",
    justificacionNecesidad: "",
    otraInformacion: "",
  });

  useEffect(() => {
    loadJobRequest();
    loadAreas();
    loadProjects();
  }, [jobRequestId]);

  // Los puestos que se pueden elegir son los del departamento de la solicitud
  // (o del que se esté eligiendo al editar, si se puede cambiar).
  useEffect(() => {
    const departamento = Number(editData.areaId);
    if (Number.isInteger(departamento) && departamento > 0) {
      loadPuestos(departamento);
    }
  }, [editData.areaId]);

  const canShowLinkTab = jobRequest?.status === JobRequestStatus.APROBADA;

  const loadPuestos = async (departamento: number) => {
    setIsLoadingPuestos(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/catalogos/puestos/public/by-area?areaId=${departamento}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const puestosData = await response.json();
      const puestosArray = Array.isArray(puestosData)
        ? puestosData
        : puestosData.data || [];
      setPuestos(puestosArray);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error loading puestos:", err);
    } finally {
      setIsLoadingPuestos(false);
    }
  };

  const loadAreas = async () => {
    setIsLoadingAreas(true);
    try {
      const areasData = await getAreasCatalog();
      setAreas(areasData.filter((area) => area.activa));
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error loading areas:", err);
    } finally {
      setIsLoadingAreas(false);
    }
  };

  // Solo para elegir proyecto al editar; si no carga, la edición sigue y el
  // proyecto actual se conserva.
  const loadProjects = async () => {
    try {
      setProjects(await getProjectsPublicAll());
    } catch (errCrudo: unknown) {
      console.error("Error loading projects:", errCrudo as Error);
    }
  };

  const loadJobRequest = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getJobRequestById(jobRequestId.toString());
      setJobRequest(data);
      // Inicializar datos de edición
      setEditData({
        puestoSolicitar: data.puestoSolicitar || "",
        areaId: data.areaId?.toString() || user?.areaId?.toString() || "",
        areaVacante: data.area?.nombre || data.areaVacante || "",
        sede: data.sede || "",
        prioridad: data.prioridad || "media",
        numeroPlazas: (data.numeroPlazas || 1).toString(),
        tipoPerfil: data.tipoPerfil || "operativo",
        tipoContrato: data.tipoContrato || "tiempo_completo",
        motivo: data.motivo || "nueva",
        previousEmployeeName: data.previousEmployeeName || "",
        fechaRequeridaInicio: data.fechaRequeridaInicio
          ? data.fechaRequeridaInicio.split("T")[0]
          : "",
        entrevistaFinalSolicitante: Boolean(data.entrevistaFinalSolicitante),
        requiresHardwareSoftware: Boolean(data.requiresHardwareSoftware),
        requiresWorkstation: Boolean(data.requiresWorkstation),
        costCenter: data.costCenter || "",
        proyectoId: data.proyectoId || data.proyecto?.id || "",
        travelPercentage:
          data.travelPercentage === null || data.travelPercentage === undefined
            ? ""
            : String(data.travelPercentage),
        salarioMinimo: data.salarioMinimo?.toString() || "",
        salarioMaximo: data.salarioMaximo?.toString() || "",
        prestaciones: data.prestaciones || "",
        habilidadesMinimas: data.habilidadesMinimas || "",
        gradoAcademico: data.gradoAcademico || "licenciatura",
        englishLevel: data.englishLevel ?? null,
        requiredSoftware: (data.requiredSoftware ?? []).map(
          (s: SoftwareRequerido) => ({
            name: s.name,
            level: s.level,
          }),
        ),
        internalCandidates: (data.internalCandidates ?? []).map(
          (c: CandidatoInterno) => ({
            name: c.name,
            currentPosition: c.currentPosition,
            isSelected: c.isSelected === true,
          }),
        ),
        aniosExperiencia:
          data.añosExperiencia !== undefined && data.añosExperiencia !== null
            ? data.añosExperiencia.toString()
            : "",
        certificacionesRequeridas: (data.certificacionesRequeridas || []).join(
          ", ",
        ),
        otrasCertificaciones: data.otrasCertificaciones || "",
        justificacionNecesidad: data.justificacionNecesidad || "",
        otraInformacion: data.otraInformacion || "",
      });
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || "Error al cargar la solicitud de vacante");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (jobRequest) {
      if (jobRequest.status !== JobRequestStatus.PENDIENTE_VALIDACION_PM) {
        setError(
          "Esta solicitud ya no se puede editar después de pre-aprobación",
        );
        return;
      }

      setEditData({
        puestoSolicitar: jobRequest.puestoSolicitar || "",
        areaId: jobRequest.areaId?.toString() || user?.areaId?.toString() || "",
        areaVacante: jobRequest.area?.nombre || jobRequest.areaVacante || "",
        sede: jobRequest.sede || "",
        prioridad: jobRequest.prioridad || "media",
        numeroPlazas: (jobRequest.numeroPlazas || 1).toString(),
        tipoPerfil: jobRequest.tipoPerfil || "operativo",
        tipoContrato: jobRequest.tipoContrato || "tiempo_completo",
        motivo: jobRequest.motivo || "nueva",
        previousEmployeeName: jobRequest.previousEmployeeName || "",
        fechaRequeridaInicio: jobRequest.fechaRequeridaInicio
          ? jobRequest.fechaRequeridaInicio.split("T")[0]
          : "",
        entrevistaFinalSolicitante: Boolean(
          jobRequest.entrevistaFinalSolicitante,
        ),
        requiresHardwareSoftware: Boolean(jobRequest.requiresHardwareSoftware),
        requiresWorkstation: Boolean(jobRequest.requiresWorkstation),
        costCenter: jobRequest.costCenter || "",
        proyectoId: jobRequest.proyectoId || jobRequest.proyecto?.id || "",
        travelPercentage:
          jobRequest.travelPercentage === null ||
          jobRequest.travelPercentage === undefined
            ? ""
            : String(jobRequest.travelPercentage),
        salarioMinimo: jobRequest.salarioMinimo?.toString() || "",
        salarioMaximo: jobRequest.salarioMaximo?.toString() || "",
        prestaciones: jobRequest.prestaciones || "",
        habilidadesMinimas: jobRequest.habilidadesMinimas || "",
        gradoAcademico: jobRequest.gradoAcademico || "licenciatura",
        englishLevel: jobRequest.englishLevel ?? null,
        requiredSoftware: (jobRequest.requiredSoftware ?? []).map((s) => ({
          name: s.name,
          level: s.level,
        })),
        internalCandidates: (jobRequest.internalCandidates ?? []).map((c) => ({
          name: c.name,
          currentPosition: c.currentPosition,
          isSelected: c.isSelected === true,
        })),
        aniosExperiencia:
          jobRequest.añosExperiencia !== undefined &&
          jobRequest.añosExperiencia !== null
            ? jobRequest.añosExperiencia.toString()
            : "",
        certificacionesRequeridas: (
          jobRequest.certificacionesRequeridas || []
        ).join(", "),
        otrasCertificaciones: jobRequest.otrasCertificaciones || "",
        justificacionNecesidad: jobRequest.justificacionNecesidad || "",
        otraInformacion: jobRequest.otraInformacion || "",
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!jobRequest) return;

    setIsSaving(true);
    setError(null);

    try {
      if (jobRequest.status !== JobRequestStatus.PENDIENTE_VALIDACION_PM) {
        throw new Error(
          "Esta solicitud ya no se puede editar después de pre-aprobación",
        );
      }

      const vacantes = parseInt(editData.numeroPlazas || "1", 10);
      const errorVacantes = errorDeVacantes(vacantes);
      if (errorVacantes) {
        // Se ataja aqui para decirlo en espanol: el 400 del backend llega con
        // el texto crudo de class-validator.
        throw new Error(errorVacantes);
      }

      if (esHtmlVacio(editData.habilidadesMinimas)) {
        throw new Error("Las habilidades mínimas son obligatorias");
      }

      const updateData: any = {
        puestoSolicitar: editData.puestoSolicitar?.trim() || null,
        areaId: editData.areaId ? parseInt(editData.areaId, 10) : undefined,
        areaVacante: editData.areaVacante?.trim() || null,
        sede: editData.sede?.trim() || null,
        prioridad: editData.prioridad,
        numeroPlazas: vacantes,
        tipoPerfil: editData.tipoPerfil,
        tipoContrato: editData.tipoContrato,
        motivo: editData.motivo,
        previousEmployeeName: editData.previousEmployeeName,
        fechaRequeridaInicio: editData.fechaRequeridaInicio || null,
        entrevistaFinalSolicitante: Boolean(
          editData.entrevistaFinalSolicitante,
        ),
        requiresHardwareSoftware: Boolean(editData.requiresHardwareSoftware),
        requiresWorkstation: Boolean(editData.requiresWorkstation),
        costCenter: editData.costCenter?.trim() || null,
        // Vacío quita el proyecto; el campo es opcional.
        proyectoId: editData.proyectoId || null,
        // Vacio es null y no cero: cero significa que el puesto no viaja.
        travelPercentage:
          String(editData.travelPercentage ?? "").trim() === ""
            ? null
            : Number(editData.travelPercentage),
        prestaciones: editData.prestaciones || null,
        habilidadesMinimas: editData.habilidadesMinimas || "",
        gradoAcademico: editData.gradoAcademico || undefined,
        justificacionNecesidad: editData.justificacionNecesidad || "",
        otraInformacion: editData.otraInformacion || null,
        otrasCertificaciones: editData.otrasCertificaciones || null,
        englishLevel: editData.englishLevel || null,
        // Las filas sin nombre no se mandan: el backend las rechazaria con un
        // 400 y lo que quiso decir quien edita es que ya no las queria.
        requiredSoftware: (editData.requiredSoftware ?? [])
          .map((s) => ({ ...s, name: s.name.trim() }))
          .filter((s) => s.name !== ""),
        internalCandidates: filasQueSeMandan(editData.internalCandidates ?? []),
      };

      if (editData.aniosExperiencia !== "") {
        updateData.añosExperiencia = parseInt(editData.aniosExperiencia, 10);
      }

      const certificaciones = (editData.certificacionesRequeridas || "")
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      updateData.certificacionesRequeridas = certificaciones;

      if (editData.salarioMinimo) {
        updateData.salarioMinimo = parseFloat(editData.salarioMinimo);
      } else {
        updateData.salarioMinimo = null;
      }

      if (editData.salarioMaximo) {
        updateData.salarioMaximo = parseFloat(editData.salarioMaximo);
      } else {
        updateData.salarioMaximo = null;
      }

      await apiService.updateJobRequest(jobRequest.id.toString(), updateData);

      setIsEditing(false);
      await loadJobRequest(); // Recargar datos
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || "Error al actualizar la solicitud");
    } finally {
      setIsSaving(false);
    }
  };

  const canEditByStatus =
    jobRequest?.status === JobRequestStatus.PENDIENTE_VALIDACION_PM;
  const canSelectAllAreas = userHasPermission(
    "generales.catalogos.areas.read",
    user?.rbac?.effectivePermissions || [],
  );

  const handleDeleteJobRequest = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteJobRequest(jobRequestId.toString());
      navigate(-1 as any);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || "Error al eliminar la solicitud");
      setIsDeleting(false);
    }
  };

  const handleActionComplete = () => {
    setSelectedAction(null);
    loadJobRequest(); // Recargar los datos
  };

  const handleActionCancel = () => {
    setSelectedAction(null);
  };

  const canUserRespond = () => {
    return (
      jobRequest?.status === JobRequestStatus.DEVUELTA &&
      jobRequest?.solicitante &&
      (user?.id === jobRequest.solicitante.id ||
        user?.email === jobRequest.solicitante.email)
    );
  };

  const getAvailableActions = (): Array<
    | "review"
    | "approve"
    | "reject"
    | "return"
    | "assign_salaries"
    | "final_approve_salary"
    | "return_to_rh"
    | "reactivate"
  > => {
    try {
      if (!jobRequest) {
        return [];
      }

      const canPreApprove = canPreApproveJobRequests(user || undefined);
      const canAssignSalaries = canAssignJobRequestSalaries(user || undefined);
      const canFinalApprove = canFinalApproveJobRequests(user || undefined);
      const canReturnInPreApproval =
        user?.rbac?.isMaster === true || user?.isAreaManager === true;
      const canReturnToRH = Boolean(
        user &&
        jobRequest?.solicitante &&
        (user.id === jobRequest.solicitante.id ||
          user.email === jobRequest.solicitante.email),
      );

      const canReject = canRejectJobRequests(user || undefined);

      switch (jobRequest.status) {
        case "pendiente_pre_aprobacion":
        case JobRequestStatus.PENDIENTE_VALIDACION_PM:
          return conRechazo(
            canPreApprove
              ? canReturnInPreApproval
                ? (["review", "approve", "return"] as const)
                : (["review", "approve"] as const)
              : [],
            canReject,
            jobRequest.status,
          );
        case JobRequestStatus.PRE_APROBADA:
          return canAssignSalaries ? ["assign_salaries"] : [];
        case JobRequestStatus.DEVUELTA_RH:
          return canAssignSalaries ? ["assign_salaries"] : [];
        case "pendiente_aprobacion_salario":
        case JobRequestStatus.EN_REVISION_SALARIOS:
        case JobRequestStatus.EN_REVISION:
          return conRechazo(
            canFinalApprove
              ? canReturnToRH
                ? (["final_approve_salary", "return_to_rh"] as const)
                : (["final_approve_salary"] as const)
              : [],
            canReject,
            jobRequest.status,
          );
        case JobRequestStatus.DEVUELTA:
          return conRechazo(
            canPreApprove ? (["approve"] as const) : [],
            canReject,
            jobRequest.status,
          );
        case JobRequestStatus.RECHAZADA:
          // SRH-317: sin este caso el bloque de acciones ni se monta para una
          // rechazada, y el boton Reactivar nunca llegaba a pintarse. Quien
          // puede reactivar es quien puede rechazar: el permiso «Eliminar
          // solicitudes».
          return canReject ? ["reactivate"] : [];
        default:
          return [];
      }
    } catch (error) {
      console.error("🔍 getAvailableActions: ERROR:", error);
      return [];
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Cargando solicitud...</p>
        </div>
      </div>
    );
  }

  if (error && !jobRequest) {
    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1 as any)}
            className={estiloDeBoton("secundario")}
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  if (!jobRequest) {
    return null;
  }

  const hasVisibleRequestActions = getAvailableActions().length > 0;

  const hasCommunicationContent =
    Boolean(jobRequest.comentariosHr) ||
    Boolean(jobRequest.motivoRechazo) ||
    Boolean(jobRequest.motivoDevolucion) ||
    Boolean(jobRequest.respuestaSolicitante) ||
    (jobRequest.status === "devuelta_rh" && Boolean(jobRequest.comentariosPM));

  const readOnlyFieldsClass = !isEditing
    ? "campos-como-texto [&_input]:border-0 [&_input]:bg-transparent [&_input]:p-0 [&_input]:shadow-none [&_input]:pointer-events-none [&_input]:focus:ring-0 [&_input]:appearance-none [&_input:disabled]:opacity-100 [&_input:disabled]:bg-transparent [&_input:disabled]:text-sm [&_input:disabled]:text-[#111111] [&_input:disabled]:cursor-default [&_select]:border-0 [&_select]:bg-transparent [&_select]:p-0 [&_select]:pr-0 [&_select]:shadow-none [&_select]:pointer-events-none [&_select]:focus:ring-0 [&_select]:appearance-none [&_select:disabled]:opacity-100 [&_select:disabled]:bg-transparent [&_select:disabled]:text-sm [&_select:disabled]:text-[#111111] [&_select:disabled]:cursor-default [&_textarea]:border-0 [&_textarea]:bg-transparent [&_textarea]:p-0 [&_textarea]:shadow-none [&_textarea]:pointer-events-none [&_textarea]:focus:ring-0 [&_textarea]:resize-none [&_textarea:disabled]:opacity-100 [&_textarea:disabled]:bg-transparent [&_textarea:disabled]:text-sm [&_textarea:disabled]:text-[#111111] [&_textarea:disabled]:cursor-default"
    : "";

  const habilidadesHtml = editData.habilidadesMinimas || "";
  const plainHabilidades = habilidadesHtml
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const getDisplayValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return "Sin asignar";
    const text = String(value).trim();
    return text.length > 0 ? text : "Sin asignar";
  };

  const getDisplayDate = (value: string | null | undefined) => {
    if (!value) return "Sin asignar";
    return formatDateSafe(value);
  };

  return (
    <div className="w-full max-w-none my-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1 as any)}
            className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Volver
          </button>
          <div>
            {/* El folio va pegado al título: es como se nombra la solicitud
                fuera de la pantalla, y suelto a la derecha quedaba a media
                altura entre el título y la ruta, sin pertenecer a ninguno. */}
            <div className="flex items-center gap-3">
              <TituloDePantalla nivel="interna">Ver solicitud</TituloDePantalla>
              <span className="inline-flex items-center rounded-md bg-[#F4F4F4] px-2.5 py-1 text-[15px] font-medium text-[#6A6B6C]">
                ID: {jobRequest.id}
              </span>
            </div>
            <p className="text-sm text-[#787878]">
              Mis solicitudes de personal / Ver detalles de solicitud
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {userHasPermission(
            "rh.solicitudes_personal.base.update",
            user?.rbac?.effectivePermissions || [],
          ) && (
            <>
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className={estiloDeBoton("secundario")}
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className={estiloDeBoton("primario")}
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? "Guardando..." : "Guardar"}
                  </button>
                </>
              ) : (
                <>
                  {/* SRH-318: disponible en cualquier estado, sobre todo en
                      las cerradas, que es cuando hace falta otra plaza igual. */}
                  <BotonDuplicarSolicitud
                    idSolicitud={jobRequest.id}
                    solicitudCargada={jobRequest}
                  />
                  {canEditByStatus && (
                    <button
                      onClick={handleStartEdit}
                      className={estiloDeBoton("primario")}
                    >
                      <Edit2 className="w-4 h-4" />
                      Editar
                    </button>
                  )}
                  {/* Mismo botón que el detalle de herramienta en almacén:
                      fondo rosa y texto rojo, sin borde. */}
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className={estiloDeBoton("peligro")}
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Información General - Ancho completo */}
      <div className={`${TARJETA} mb-6 ${readOnlyFieldsClass}`}>
        {/* Header con título y badge */}
        {/* La linea llega a los bordes de la tarjeta y no al area con padding:
            el `-mx-6` saca al bloque del padding y el `px-6` le devuelve al
            texto la alineacion del resto del contenido. */}
        <div className="-mx-6 mb-6 flex items-center justify-between gap-4 border-b border-[#E0E0E0] px-6 pb-4">
          <h2 className="text-base font-semibold text-[#111111]">
            Información general
          </h2>
          {/* Sin borde de color: la pastilla se homologa a su propio fondo.
              El componente es compartido por 21 pantallas, asi que el borde se
              apaga aqui en vez de quitarselo a todas. */}
          <StatusBadge
            status={jobRequest.status as any}
            className="border-transparent"
          />
        </div>

        {/* Grid de 4 columnas */}
        <div className="grid grid-cols-4 gap-x-8 gap-y-6 mb-6">
          {/* Fila 1 */}
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">Puesto:</label>
            {isLoadingPuestos ? (
              <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
                Cargando puestos...
              </div>
            ) : (
              <SelectBuscable
                variante="formulario"
                value={editData.puestoSolicitar}
                onChange={(valor) =>
                  setEditData({ ...editData, puestoSolicitar: valor })
                }
                disabled={!isEditing}
                placeholder="Selecciona un puesto"
                placeholderDeBusqueda="Buscar puesto..."
                opciones={[
                  ...(editData.puestoSolicitar &&
                  !puestos.some(
                    (p: any) =>
                      (p.nombre || p.name) === editData.puestoSolicitar,
                  )
                    ? [
                        {
                          value: editData.puestoSolicitar,
                          label: editData.puestoSolicitar,
                        },
                      ]
                    : []),
                  ...puestos.map((puesto: any) => ({
                    value: String(puesto.nombre || puesto.name),
                    label: String(puesto.nombre || puesto.name),
                  })),
                ]}
              />
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Departamento/Área solicitante:
            </label>
            {isLoadingAreas ? (
              <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
                Cargando áreas...
              </div>
            ) : (
              <SelectBuscable
                variante="formulario"
                value={editData.areaId}
                onChange={(selectedId) => {
                  const selectedArea = areas.find(
                    (a) => a.id.toString() === selectedId,
                  );
                  setEditData({
                    ...editData,
                    areaId: selectedId,
                    areaVacante: selectedArea?.nombre || editData.areaVacante,
                  });
                }}
                disabled={!isEditing || !canSelectAllAreas}
                placeholder="Selecciona un área"
                placeholderDeBusqueda="Buscar área..."
                opciones={areas.map((area) => ({
                  value: String(area.id),
                  label: area.nombre,
                }))}
              />
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">Sede:</label>
            <input
              type="text"
              value={isEditing ? editData.sede : getDisplayValue(editData.sede)}
              onChange={(e) =>
                setEditData({ ...editData, sede: e.target.value })
              }
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Prioridad:
            </label>
            <SelectBuscable
              variante="formulario"
              value={editData.prioridad}
              onChange={(valor) =>
                setEditData({ ...editData, prioridad: valor })
              }
              disabled={!isEditing}
              canDelete={false}
              placeholderDeBusqueda="Buscar prioridad..."
              opciones={[
                { value: "baja", label: "Baja" },
                { value: "media", label: "Media" },
                { value: "alta", label: "Alta" },
                { value: "urgente", label: "Urgente" },
              ]}
            />
          </div>

          {/* Fila 2 */}
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Número de vacantes:
            </label>
            <input
              type={isEditing ? "number" : "text"}
              min={MINIMO_DE_VACANTES}
              max={MAXIMO_DE_VACANTES}
              value={
                isEditing
                  ? editData.numeroPlazas
                  : getDisplayValue(editData.numeroPlazas)
              }
              onChange={(e) =>
                setEditData({ ...editData, numeroPlazas: e.target.value })
              }
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Tipo de puesto:
            </label>
            <SelectBuscable
              variante="formulario"
              value={editData.tipoPerfil}
              onChange={(valor) =>
                setEditData({ ...editData, tipoPerfil: valor })
              }
              disabled={!isEditing}
              canDelete={false}
              placeholderDeBusqueda="Buscar tipo de puesto..."
              opciones={[
                { value: "operativo", label: "Operativo" },
                { value: "tecnico", label: "Técnico" },
                { value: "administrativo", label: "Administrativo" },
              ]}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Tipo de Contrato:
            </label>
            <SelectBuscable
              variante="formulario"
              value={editData.tipoContrato}
              onChange={(valor) =>
                setEditData({ ...editData, tipoContrato: valor })
              }
              disabled={!isEditing}
              canDelete={false}
              placeholderDeBusqueda="Buscar contrato..."
              opciones={TIPOS_DE_CONTRATO}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">Motivo:</label>
            <SelectBuscable
              variante="formulario"
              value={editData.motivo}
              onChange={(valor) => setEditData({ ...editData, motivo: valor })}
              disabled={!isEditing}
              canDelete={false}
              placeholderDeBusqueda="Buscar motivo..."
              opciones={MOTIVOS_DE_SOLICITUD}
            />
          </div>

          {/* Solo tiene sentido en un reemplazo, así que solo ahí aparece. */}
          {editData.motivo === "reemplazo" && (
            <div>
              <label className="mb-1 block text-xs text-[#6A6B6C]">
                Empleado anterior:
              </label>
              <input
                type="text"
                value={editData.previousEmployeeName ?? ""}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    previousEmployeeName: e.target.value,
                  })
                }
                disabled={!isEditing}
                maxLength={255}
                placeholder="Nombre de quien deja la posición"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Fila 3 */}
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Fecha requerida de inicio:
            </label>
            <input
              type={isEditing ? "date" : "text"}
              value={
                isEditing
                  ? editData.fechaRequeridaInicio
                  : getDisplayDate(editData.fechaRequeridaInicio)
              }
              onChange={(e) =>
                setEditData({
                  ...editData,
                  fechaRequeridaInicio: e.target.value,
                })
              }
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Entrevista final por solicitante:
            </label>
            {isEditing ? (
              <label className="flex items-center h-10">
                <Checkbox
                  checked={Boolean(editData.entrevistaFinalSolicitante)}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      entrevistaFinalSolicitante: e.target.checked,
                    })
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-900">
                  Requiere segunda entrevista
                </span>
              </label>
            ) : (
              <p className="text-sm text-[#111111]">
                {jobRequest.entrevistaFinalSolicitante ? "Sí" : "No"}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Centro de costo:
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editData.costCenter ?? ""}
                onChange={(e) =>
                  setEditData({ ...editData, costCenter: e.target.value })
                }
                maxLength={100}
                placeholder="ej. CC-001"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-sm text-[#111111]">
                {jobRequest.costCenter || "No especificado"}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Porcentaje de viaje:
            </label>
            {isEditing ? (
              <input
                type="number"
                value={editData.travelPercentage ?? ""}
                onChange={(e) =>
                  setEditData({ ...editData, travelPercentage: e.target.value })
                }
                min={0}
                max={100}
                step={1}
                placeholder="ej. 25"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              /* Cero es una respuesta, asi que se compara contra nulo. */
              <p className="text-sm text-[#111111]">
                {jobRequest.travelPercentage === null ||
                jobRequest.travelPercentage === undefined
                  ? "No especificado"
                  : `${jobRequest.travelPercentage}%`}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Requiere hardware o software:
            </label>
            {isEditing ? (
              <label className="flex items-center h-10">
                <Checkbox
                  checked={Boolean(editData.requiresHardwareSoftware)}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      requiresHardwareSoftware: e.target.checked,
                    })
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-900">
                  Hay que preparar equipo de computo o licencias
                </span>
              </label>
            ) : (
              <p className="text-sm text-[#111111]">
                {jobRequest.requiresHardwareSoftware ? "Sí" : "No"}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Requiere equipo y escritorio:
            </label>
            {isEditing ? (
              <label className="flex items-center h-10">
                <Checkbox
                  checked={Boolean(editData.requiresWorkstation)}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      requiresWorkstation: e.target.checked,
                    })
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-900">
                  Hay que preparar el lugar de trabajo
                </span>
              </label>
            ) : (
              <p className="text-sm text-[#111111]">
                {jobRequest.requiresWorkstation ? "Sí" : "No"}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-[#6A6B6C]">
              Solicitado por:
            </label>
            <p className="text-sm text-[#111111]">
              {jobRequest.solicitante
                ? `${jobRequest.solicitante.firstName} ${jobRequest.solicitante.lastName}`
                : "No especificado"}
            </p>
          </div>
          {(isEditing || jobRequest.proyecto) && (
            <div>
              <label className="mb-1 block text-xs text-[#6A6B6C]">
                Proyecto/Centro de costos:
              </label>
              {isEditing ? (
                <SelectBuscable
                  variante="formulario"
                  value={editData.proyectoId}
                  onChange={(valor) =>
                    setEditData({ ...editData, proyectoId: valor })
                  }
                  placeholder="Sin proyecto"
                  placeholderDeBusqueda="Buscar proyecto…"
                  aria-label="Proyecto/Centro de costos"
                  opciones={[
                    { value: "", label: "Sin proyecto" },
                    ...projects.map((project) => ({
                      value: project.id,
                      label: project.name,
                    })),
                  ]}
                />
              ) : (
                <p className="text-sm text-[#111111]">
                  {jobRequest.proyecto?.name}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pestañas del diseño: subrayado en la activa, no botones rellenos. */}
      <div className="mb-6 border-b border-[#E0E0E0]">
        <nav
          className="-mb-px flex flex-wrap gap-6"
          aria-label="Secciones de la solicitud"
        >
          {pestanasVisibles.map((pestana) => (
            <button
              key={pestana.clave}
              type="button"
              onClick={() => setActiveTab(pestana.clave)}
              aria-current={activeTab === pestana.clave ? "page" : undefined}
              className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm transition-colors ${
                activeTab === pestana.clave
                  ? "border-[#477FFA] font-medium text-[#111111]"
                  : "border-transparent text-[#6A6B6C] hover:text-[#111111]"
              }`}
            >
              {pestana.etiqueta}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "perfil" && (
        <div className={`${TARJETA} ${readOnlyFieldsClass}`}>
          {/* Requisitos del perfil */}
          <div className="">
            {/* Lo que pide el puesto, en la tarjeta gris del diseño. */}
            <div
              className={`grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-3 ${CAJA_GRIS}`}
            >
              <h3 className="text-base font-semibold text-[#111111] md:col-span-3">
                Requisitos del perfil
              </h3>
              {/* `contents` deshace este contenedor para que escolaridad y años
              caigan como celdas de la rejilla de arriba, sin reindentar el
              bloque entero. */}
              <div className="contents">
                <div>
                  <label className="mb-1 block text-xs text-[#6A6B6C]">
                    Escolaridad requerida:
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    value={editData.gradoAcademico}
                    onChange={(valor) =>
                      setEditData({ ...editData, gradoAcademico: valor })
                    }
                    disabled={!isEditing}
                    canDelete={false}
                    placeholderDeBusqueda="Buscar escolaridad..."
                    opciones={NIVELES_DE_ESCOLARIDAD}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs text-[#6A6B6C]">
                    Años de experiencia requeridos:
                  </label>
                  <input
                    type={isEditing ? "number" : "text"}
                    min={0}
                    max={50}
                    value={
                      isEditing
                        ? editData.aniosExperiencia
                        : getDisplayValue(editData.aniosExperiencia)
                    }
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        aniosExperiencia: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Idioma y paquetería (RH-02). Mismo acomodo y mismas clases que en
              el alta, y los controles se apagan en vez de cambiar a texto: es
              como se comporta el resto de los campos de esta pantalla. */}
              <div>
                <label className="mb-1 block text-xs text-[#6A6B6C]">
                  Nivel de inglés requerido
                </label>
                <SelectBuscable
                  variante="formulario"
                  value={editData.englishLevel ?? ""}
                  onChange={(valor) =>
                    setEditData({
                      ...editData,
                      englishLevel: (valor || null) as NivelDeIngles | null,
                    })
                  }
                  disabled={!isEditing}
                  className="max-w-xs"
                  placeholderDeBusqueda="Buscar nivel..."
                  opciones={[
                    { value: "", label: "No requerido" },
                    ...NIVELES_DE_INGLES,
                  ]}
                />
              </div>

              <div className="md:col-span-3">
                <PaqueteriaRequerida
                  filas={editData.requiredSoftware ?? []}
                  onChange={(filas) =>
                    setEditData({ ...editData, requiredSoftware: filas })
                  }
                  deshabilitado={!isEditing}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs text-[#6A6B6C]">
                  Certificaciones requeridas:
                </label>
                <>
                  <input
                    type="text"
                    value={
                      isEditing
                        ? editData.certificacionesRequeridas
                        : getDisplayValue(editData.certificacionesRequeridas)
                    }
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        certificacionesRequeridas: e.target.value,
                      })
                    }
                    placeholder={
                      isEditing
                        ? "Ej. AWS, Scrum Master, PMP (separar con comas)"
                        : "Sin asignar"
                    }
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isEditing && (
                    <p className="text-sm text-gray-500 mt-1">
                      Opcional. Separar múltiples certificaciones con comas.
                    </p>
                  )}
                </>
              </div>

              <div>
                <label className="mb-1 block text-xs text-[#6A6B6C]">
                  Otras certificaciones:
                </label>
                <textarea
                  value={
                    isEditing
                      ? editData.otrasCertificaciones
                      : getDisplayValue(editData.otrasCertificaciones)
                  }
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      otrasCertificaciones: e.target.value,
                    })
                  }
                  rows={3}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* La descripcion publicada: el texto largo que ve el candidato. */}
            <div className="mt-6">
              <p className="mb-3 border-b border-[#E0E0E0] pb-3 text-sm text-[#111111]">
                Descripción publicada · {jobRequest.puestoSolicitar}
              </p>
              {/* La etiqueta de este campo ahora es el encabezado de arriba. */}
              <div>
                {isEditing ? (
                  <RichTextEditor
                    value={editData.habilidadesMinimas}
                    onChange={(value) =>
                      setEditData({ ...editData, habilidadesMinimas: value })
                    }
                    placeholder="Describe habilidades mínimas..."
                    disabled={!isEditing}
                  />
                ) : (
                  <>
                    <div
                      className="rich-text-content text-gray-900"
                      dangerouslySetInnerHTML={{
                        __html: plainHabilidades
                          ? separaElLlamado(habilidadesHtml).cuerpo
                          : "No especificado",
                      }}
                    />

                    {/* El cierre «Postúlate» va como aviso, al final de todo. Sale
                    del mismo texto de la descripción, no es un campo aparte. */}
                    {plainHabilidades &&
                      separaElLlamado(habilidadesHtml).llamado && (
                        <div className="mt-5 flex items-start gap-3 rounded-lg bg-[#EBF8FC] px-4 py-3 text-sm text-[#2E6380]">
                          <Send className="mt-0.5 h-4 w-4 flex-shrink-0" />
                          <div
                            className="rich-text-content [&_*]:my-0 [&_*]:text-[#2E6380]"
                            dangerouslySetInnerHTML={{
                              __html: enUnaLinea(
                                separaElLlamado(habilidadesHtml).llamado,
                              ),
                            }}
                          />
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Información adicional */}
          <div className="mt-6 pt-6 border-t border-[#E0E0E0]">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Información Adicional
            </h3>

            <div className="mb-4">
              <label className="mb-1 block text-xs text-[#6A6B6C]">
                Justificación de la necesidad:
              </label>
              <TextareaQueCrece
                value={
                  isEditing
                    ? editData.justificacionNecesidad
                    : getDisplayValue(editData.justificacionNecesidad)
                }
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    justificacionNecesidad: e.target.value,
                  })
                }
                filasMinimas={4}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-[#6A6B6C]">
                Otra información:
              </label>
              <TextareaQueCrece
                value={
                  isEditing
                    ? editData.otraInformacion
                    : getDisplayValue(editData.otraInformacion)
                }
                onChange={(e) =>
                  setEditData({ ...editData, otraInformacion: e.target.value })
                }
                filasMinimas={3}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "compensacion" && verCompensacion && (
        <div className={`${TARJETA} ${readOnlyFieldsClass}`}>
          {/* Prestaciones y Compensación - Nueva sección completa debajo del grid */}
          <div className="">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-500">
                Condiciones Económicas:
              </label>
            </div>

            {/* Rango Salarial. En consulta va como un solo dato resaltado, que es
              lo que pide el diseño; al editar vuelven los dos campos, porque
              ahí se captura cada extremo por separado. */}
            {!isEditing &&
              rangoSalarial(editData.salarioMinimo, editData.salarioMaximo) && (
                <div className="mb-4 inline-block rounded-xl bg-[#E8F8FE] px-5 py-4">
                  <p className="text-xs text-[#1E1F1F]">
                    Rango salarial mensual:
                  </p>
                  <p className="mt-1 text-xl font-semibold text-[#1E1F1F]">
                    {rangoSalarial(
                      editData.salarioMinimo,
                      editData.salarioMaximo,
                    )}
                  </p>
                </div>
              )}

            <div className={`mb-4 ${!isEditing ? "hidden" : ""}`}>
              <label className="mb-1 block text-xs text-[#6A6B6C]">
                Salario Mínimo (MXN) - Salario Máximo (MXN)
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type={isEditing ? "number" : "text"}
                  placeholder={isEditing ? "Salario mínimo" : "Sin asignar"}
                  value={
                    isEditing
                      ? editData.salarioMinimo
                      : getDisplayValue(editData.salarioMinimo)
                  }
                  onChange={(e) =>
                    setEditData({ ...editData, salarioMinimo: e.target.value })
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">–</span>
                <input
                  type={isEditing ? "number" : "text"}
                  placeholder={isEditing ? "Salario máximo" : "Sin asignar"}
                  value={
                    isEditing
                      ? editData.salarioMaximo
                      : getDisplayValue(editData.salarioMaximo)
                  }
                  onChange={(e) =>
                    setEditData({ ...editData, salarioMaximo: e.target.value })
                  }
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Prestaciones */}
            <div>
              <label className="mb-1 block text-xs text-[#6A6B6C]">
                Prestaciones de la vacante:
              </label>
              <textarea
                value={
                  isEditing
                    ? editData.prestaciones
                    : getDisplayValue(editData.prestaciones)
                }
                onChange={(e) =>
                  setEditData({ ...editData, prestaciones: e.target.value })
                }
                rows={4}
                placeholder={
                  isEditing
                    ? "Describe las prestaciones ofrecidas..."
                    : "Sin asignar"
                }
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}
      {/* Contenido de la pestaña «Estado de la solicitud y enlace» */}
      {activeTab === "estado" && (
        <div className="space-y-6">
          {/* Las tres columnas del diseño en una sola tarjeta: por donde va la
              solicitud, cuándo se movió y el enlace para los candidatos. */}
          <div className={`${TARJETA} grid grid-cols-1 gap-6 md:grid-cols-3`}>
            {/* La linea de tiempo ya trae su propio titulo; poner otro aqui
                dejaba dos encabezados seguidos diciendo lo mismo. */}
            <div className={CAJA_GRIS}>
              <RequestTimeline request={jobRequest} />
            </div>

            <div className={CAJA_GRIS}>
              <h3 className="mb-4 text-base font-semibold text-[#111111]">
                Información de fechas
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Creada
                  </label>
                  <p className="text-sm text-gray-900">
                    {formatDateTimeMX(jobRequest.fechaCreacion)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Última Actualización
                  </label>
                  <p className="text-sm text-gray-900">
                    {formatDateTimeMX(jobRequest.fechaActualizacion)}
                  </p>
                </div>
                {jobRequest.fechaRevision && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Fecha de Revisión
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDateTimeMX(jobRequest.fechaRevision)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* El enlace solo existe cuando la vacante ya está aprobada. Va sin
                caja gris: el recuadro del link ya es su propio contenedor. */}
            {canShowLinkTab && (
              <div>
                <h3 className="mb-4 text-base font-semibold text-[#111111]">
                  Compartir enlace
                </h3>
                <CandidatePublicLink jobRequestId={jobRequest.id} />
              </div>
            )}
          </div>

          {/* Lo que se conversa y lo que se puede hacer con la solicitud. */}
          <div>
            {hasCommunicationContent && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                {jobRequest.comentariosHr && (
                  <div className="mb-4 p-4 bg-white rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">
                      Comentarios de RH
                    </h4>
                    <p className="text-blue-800">{jobRequest.comentariosHr}</p>
                    {jobRequest.revisadoPor && (
                      <p className="text-sm text-blue-600 mt-2">
                        Por: {jobRequest.revisadoPor.firstName}{" "}
                        {jobRequest.revisadoPor.lastName}
                      </p>
                    )}
                  </div>
                )}

                {jobRequest.motivoRechazo && (
                  <div className="mb-4 p-4 bg-red-50 rounded-lg">
                    <h4 className="font-medium text-red-900 mb-2">
                      Motivo de Rechazo
                    </h4>
                    <p className="text-red-800">{jobRequest.motivoRechazo}</p>
                  </div>
                )}

                {jobRequest.motivoDevolucion && (
                  <div className="mb-4 p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-medium text-yellow-900 mb-2">
                      Motivo de Devolución
                    </h4>
                    <p className="text-yellow-800">
                      {jobRequest.motivoDevolucion}
                    </p>
                  </div>
                )}

                {jobRequest.respuestaSolicitante && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">
                      Respuesta del Solicitante
                    </h4>
                    <p className="text-green-800">
                      {jobRequest.respuestaSolicitante}
                    </p>
                  </div>
                )}

                {/* Comentarios del PM cuando devuelve a RH */}
                {jobRequest.status === "devuelta_rh" &&
                  jobRequest.comentariosPM && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-amber-900 mb-2">
                        Comentarios del Project Manager
                      </h4>
                      <p className="text-amber-800">
                        {jobRequest.comentariosPM}
                      </p>
                      {jobRequest.fechaDevolucionRH && (
                        <p className="text-sm text-amber-600 mt-2">
                          Devuelta el:{" "}
                          {formatDateTimeMX(jobRequest.fechaDevolucionRH)}
                        </p>
                      )}
                    </div>
                  )}
              </div>
            )}

            {/* Componente para responder si está devuelta */}
            {canUserRespond() && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <RespondToReturned
                  request={jobRequest}
                  onSuccess={loadJobRequest}
                />
              </div>
            )}

            {/* Acciones del flujo de doble etapa */}
            {hasVisibleRequestActions && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-base font-medium text-gray-900 mb-4">
                  Acciones de Solicitud
                </h3>
                <JobRequestActions
                  jobRequest={jobRequest}
                  onUpdate={loadJobRequest}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contenido de la tab "Candidatos" */}
      {activeTab === "candidatos" && (
        <div className="space-y-6">
          {/* Los de casa primero: son pocos y se deciden antes que los de
              fuera. La tabla de abajo es la de los candidatos externos. */}
          <div className={`${TARJETA} ${readOnlyFieldsClass}`}>
            <div className="mt-6">
              <label className="mb-1 block text-xs text-[#6A6B6C]">
                Candidatos internos:
              </label>
              <CandidatosInternos
                filas={editData.internalCandidates ?? []}
                onChange={(filas) =>
                  setEditData({ ...editData, internalCandidates: filas })
                }
                deshabilitado={!isEditing}
              />
              {/* SRH-313: lo que se ve es lo guardado, no lo que se esta editando. */}
              {!isEditing && (
                <ConfirmacionDeInterno
                  jobRequestId={jobRequest.id}
                  internos={jobRequest.internalCandidates}
                  estatusDeLaVacante={jobRequest.status}
                  permisos={user?.rbac?.effectivePermissions}
                  onConfirmado={loadJobRequest}
                />
              )}
            </div>
          </div>

          {/* Sin tarjeta alrededor: la tabla ya trae su propio marco, y
              envolverla dejaba un borde dentro de otro. */}
          <JobRequestCandidatesTab jobRequestId={jobRequest.id} />
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && (
        <CapaDeModal>
          <div className="relative p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">
                Confirmar Eliminación
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  ¿Estás seguro de que quieres eliminar esta solicitud de
                  vacante? Esta acción no se puede deshacer.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className={estiloDeBoton("secundario")}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteJobRequest}
                  disabled={isDeleting}
                  className={estiloDeBoton("peligro")}
                >
                  {isDeleting ? "Eliminando..." : "Eliminar"}
                </button>
              </div>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal de acciones */}
      {selectedAction && (
        <ActionModal
          isOpen={true}
          action={selectedAction}
          requestId={jobRequest.id.toString()}
          onSuccess={handleActionComplete}
          onCancel={handleActionCancel}
        />
      )}
    </div>
  );
};

export default JobRequestDetail;
